import React, { useEffect, useState } from 'react';
import { Sparkles, AlertTriangle, Music } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { computeQualityScore, summarizeWarnings } from '@/utils/setWarnings';

const TONE_STYLES = {
  green: {
    ring: '#10B981',
    ringBg: 'rgba(16,185,129,0.15)',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50/70',
    border: 'border-emerald-200',
    chip: 'bg-emerald-500',
  },
  blue: {
    ring: '#2563FF',
    ringBg: 'rgba(37,99,255,0.15)',
    text: 'text-blue-700',
    bg: 'bg-blue-50/70',
    border: 'border-blue-200',
    chip: 'bg-blue-500',
  },
  amber: {
    ring: '#F59E0B',
    ringBg: 'rgba(245,158,11,0.15)',
    text: 'text-amber-700',
    bg: 'bg-amber-50/70',
    border: 'border-amber-200',
    chip: 'bg-amber-500',
  },
  red: {
    ring: '#EF4444',
    ringBg: 'rgba(239,68,68,0.15)',
    text: 'text-red-700',
    bg: 'bg-red-50/70',
    border: 'border-red-200',
    chip: 'bg-red-500',
  },
};

// Tiny CSS-only confetti — six gradient sparks pop from center outward.
function CelebrationConfetti() {
  const sparks = [
    { x: 60, y: -40, c: '#2563FF', d: 0.0 },
    { x: -60, y: -50, c: '#14E6C9', d: 0.05 },
    { x: 80, y: 20, c: '#A855F7', d: 0.1 },
    { x: -80, y: 30, c: '#F59E0B', d: 0.08 },
    { x: 30, y: -70, c: '#22D3EE', d: 0.12 },
    { x: -30, y: -75, c: '#EC4899', d: 0.06 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-visible">
      {sparks.map((s, i) => (
        <motion.span
          key={i}
          className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full"
          style={{ background: s.c, boxShadow: `0 0 10px ${s.c}` }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 0.4 }}
          animate={{ x: s.x, y: s.y, opacity: 0, scale: 1.2 }}
          transition={{ duration: 1.2, delay: s.d, ease: 'easeOut' }}
        />
      ))}
    </div>
  );
}

/**
 * Mix Quality Score card — compact (assistant chat) or full (detail page).
 * Props:
 *   warnings   array of warning objects (severity-tagged)
 *   trackCount total tracks in set
 *   variant    'compact' | 'full'  (default 'compact')
 *   celebrate  trigger one-time confetti burst on mount (for new builds)
 */
export default function MixQualityCard({
  warnings = [],
  trackCount = 0,
  variant = 'compact',
  celebrate = false,
}) {
  const { score, verdict, tone } = computeQualityScore(warnings);
  const sums = summarizeWarnings(warnings);
  const t = TONE_STYLES[tone];
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (celebrate && score >= 85) {
      setShowConfetti(true);
      const id = setTimeout(() => setShowConfetti(false), 1400);
      return () => clearTimeout(id);
    }
  }, [celebrate, score]);

  // SVG ring math — circumference 2πr, r = 24
  const R = 24;
  const C = 2 * Math.PI * R;
  const dash = (score / 100) * C;

  if (variant === 'compact') {
    return (
      <div
        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl border ${t.bg} ${t.border}`}
        data-testid="mix-quality-card-compact"
      >
        {showConfetti && <CelebrationConfetti />}

        {/* Ring */}
        <div className="relative w-14 h-14 flex-shrink-0">
          <svg width="56" height="56" viewBox="0 0 56 56" className="rotate-[-90deg]">
            <circle cx="28" cy="28" r={R} stroke={t.ringBg} strokeWidth="5" fill="none" />
            <motion.circle
              cx="28"
              cy="28"
              r={R}
              stroke={t.ring}
              strokeWidth="5"
              fill="none"
              strokeLinecap="round"
              initial={{ strokeDasharray: `0 ${C}` }}
              animate={{ strokeDasharray: `${dash} ${C}` }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-[15px] font-heading font-bold ${t.text}`} data-testid="mix-quality-score">
              {score}
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {score >= 85 ? (
              <Sparkles className={`w-3.5 h-3.5 ${t.text}`} />
            ) : (
              <AlertTriangle className={`w-3.5 h-3.5 ${t.text}`} />
            )}
            <p className={`font-heading text-xs font-semibold uppercase tracking-wide ${t.text}`}>
              {verdict}
            </p>
          </div>
          <p className="text-[10px] text-[#64748B] font-mono leading-snug">
            Quality {score}/100
            {' · '}
            {warnings.length === 0
              ? 'no warnings'
              : `${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
            {trackCount > 0 && ` · ${trackCount} tracks`}
          </p>
        </div>

        {warnings.length > 0 && (
          <div className="flex flex-col items-end gap-0.5">
            {sums.high > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-red-500 text-white text-[9px] font-bold leading-none">{sums.high}H</span>
            )}
            {sums.med > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px] font-bold leading-none">{sums.med}M</span>
            )}
            {sums.low > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-blue-500 text-white text-[9px] font-bold leading-none">{sums.low}L</span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full variant — larger ring, used inside PlaylistDetailPage hero panel
  return (
    <div
      className={`relative flex items-center gap-5 p-5 rounded-2xl border ${t.bg} ${t.border}`}
      data-testid="mix-quality-card-full"
    >
      <AnimatePresence>{showConfetti && <CelebrationConfetti />}</AnimatePresence>

      <div className="relative w-20 h-20 flex-shrink-0">
        <svg width="80" height="80" viewBox="0 0 56 56" className="rotate-[-90deg]">
          <circle cx="28" cy="28" r={R} stroke={t.ringBg} strokeWidth="5" fill="none" />
          <motion.circle
            cx="28"
            cy="28"
            r={R}
            stroke={t.ring}
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
            initial={{ strokeDasharray: `0 ${C}` }}
            animate={{ strokeDasharray: `${dash} ${C}` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-heading font-bold ${t.text}`}>{score}</span>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {score >= 85 ? (
            <Sparkles className={`w-4 h-4 ${t.text}`} />
          ) : (
            <AlertTriangle className={`w-4 h-4 ${t.text}`} />
          )}
          <p className={`font-heading text-sm font-bold uppercase tracking-wide ${t.text}`}>
            {verdict}
          </p>
          <span className="text-[10px] font-mono text-[#94A3B8]">· Mix Quality Score</span>
        </div>
        <p className="text-xs text-[#475569] font-body">
          {warnings.length === 0
            ? `Clean transitions across all ${trackCount} tracks — no BPM jumps, key clashes, or energy spikes detected.`
            : `${warnings.length} transition warning${warnings.length === 1 ? '' : 's'} across ${trackCount} tracks.`}
        </p>
        {warnings.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2">
            {sums.high > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-red-500 text-white text-[10px] font-semibold">{sums.high} HIGH</span>
            )}
            {sums.med > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px] font-semibold">{sums.med} MED</span>
            )}
            {sums.low > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-blue-500 text-white text-[10px] font-semibold">{sums.low} LOW</span>
            )}
          </div>
        )}
      </div>

      <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl bg-white/60 backdrop-blur border border-white/70">
        <Music className="w-4 h-4 text-[#64748B]" />
        <span className="text-[11px] font-mono text-[#475569]">{trackCount} tracks</span>
      </div>
    </div>
  );
}
